import { AccessLevel, AccountOptions, ProductCode, ProductType } from '@dev/ee-api/ee-api-models';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import { Location } from 'history';
import { includes, isEmpty, omit, pick } from 'lodash-es';
import { parse, stringify } from 'query-string';
import * as React from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, RouteProps } from 'react-router-dom';
import { RouterState } from 'redux-first-history';

import { FeatureUpselling } from '@/components/FeatureUpselling';
import { OnboardingSurvey } from '@/components/Surveys/OnboardingSurvey';
import { APIPaths, DashboardPaths, HomePaths, SettingsPaths, TemplatesPaths } from '@/services/dictionaries/Paths';
import { URLSearchParamsEnum } from '@/services/dictionaries/UrlSearchParams';
import { isAccountActivated } from '@/services/Secuirty';
import { ApplicationState as AppState, getConfigStore } from '@/store';
import { AccountState } from '@/store/account/types';
import { SessionFailedAuth } from '@/store/session/actions';
import DragDropEditor from '../../../components/EditorDragDrop';
import Authentication from '../../Authentication';
import { useCurrentPlanStatus } from '../../hooks/useCurrentPlanStatus';
import { useDashboardElementVisibility } from '../../hooks/useDashboardElementVisibility';
import { useUpselling } from '../../hooks/useUpselling';

const isAllowedLegacyPath = (location: Location): boolean => {
    const pathname = location.pathname;
    const search = location.search;

    if (!pathname.includes('/legacy/')) {
        return true;
    }

    return (
        pathname.includes('/legacy/#/help') || (pathname.includes('/legacy/#/account') && search.includes('buyip=true'))
    );
};

interface PrivateRouteInterface extends RouteProps {
    accessLevel?: AccessLevel[];
    account?: AccountState;
    router?: RouterState;
    disableBanners?: boolean;
    preventProductSwitching?: boolean;
    upsellingFeature?: AvailableFeaturesUpselling;
}
const doNotSetReturnUrlOnThose = ['/login', '/create-account'];

const parseQueryParams = (routerLocation?: any): Record<'returnUrl' | 'token' | 'urlParams', string> => {
    const queryParams = parse(routerLocation.search);
    const parsedQueryParams = omit(queryParams, ['t', 'message']);
    let returnUrl = routerLocation.pathname;
    let token = '';

    if (!isEmpty(parsedQueryParams)) {
        returnUrl += `?${stringify(parsedQueryParams)}`;
    }
    if (queryParams.t) {
        token = stringify(pick(queryParams, ['t']));
    }

    return {
        returnUrl,
        token,
        urlParams: stringify(omit(queryParams, ['t'])),
    };
};

const changeDashboardPaths = (url: string, productCode: ProductCode | ProductType) => {
    if (location.href.includes(URLSearchParamsEnum.ShareIdentifier)) {
        return url.replace(`${DashboardPaths.Marketing}`, DashboardPaths?.[productCode]);
    }

    return DashboardPaths[productCode];
};

const mapState = (state: AppState, ownProps: PrivateRouteInterface): PrivateRouteInterface => ({
    ...ownProps,
    account: state.account,
    router: state.router,
});

// eslint-disable-next-line sonarjs/cognitive-complexity
const PrivateRoute: React.FC<PrivateRouteInterface> = (params): JSX.Element => {
    const { returnUrl, token, urlParams } = parseQueryParams(params?.router?.location);
    const { bootedAsChild, isStandalone, routerBasePath } = useSelector(getConfigStore);
    const { isUpsellingVisible } = useUpselling();
    const { productCode } = useCurrentPlanStatus();
    const { isCreatorMenuVisible } = useDashboardElementVisibility();
    const dispatch = useDispatch();

    if (!Authentication.isAuthenticated()) {
        const to = {
            pathname: HomePaths.Login,
            search: token || urlParams,
        };
        if (sessionStorage && !doNotSetReturnUrlOnThose.includes(window.location.pathname)) {
            sessionStorage.setItem('returnUrl', returnUrl);
        }
        dispatch(SessionFailedAuth());
        return <Redirect to={to} />;
    }

    const { account, preventProductSwitching } = params;

    if (productCode === ProductCode.CreatorSuite && !isAllowedLegacyPath(params.location as Location)) {
        return <Redirect to="/404" />;
    }

    // These URLs are blocked for CreatorSuite plan users even though they have the required access level.
    // This is because these features should not be available for CreatorSuite users despite their access level.
    // The access level check cannot be used here because these plans have the required access level,
    // but they should not see these screens regardless of their access level.
    const blockedPathsForCreatorProduct = [
        APIPaths.Statistics,
        APIPaths.StatisticsInprogress,
        APIPaths.LogsEmail,
        TemplatesPaths.EditorLink,
        SettingsPaths.CreateAPI,
        SettingsPaths.DomainsAdvanced,
        SettingsPaths.ManageInbounds,
        SettingsPaths.ManageSMTP,
        SettingsPaths.ManageWebhooks,
        SettingsPaths.CreateWebhook,
        SettingsPaths.ManageAPI,
        SettingsPaths.CreateSMTP,
        SettingsPaths.CreateInbound,
    ];

    const isBlockedPath = blockedPathsForCreatorProduct.some((path) => params.location.pathname === path);

    if (productCode === ProductCode.CreatorSuite && isCreatorMenuVisible && isBlockedPath) {
        console.log('Restricted due to product.', params?.location?.pathname);
        return <Redirect to={HomePaths.Unauthorized} />;
    }

    if (!params.account.accountLoading && params.account && params.account.GeneralInfo) {
        const { AccountAccessLevel, Options } = params.account.GeneralInfo;
        const accountHasAccessLevel = params.accessLevel?.every((singleAccessLevel): boolean =>
            AccountAccessLevel.includes(singleAccessLevel)
        );

        const canSeeUpselling = params.upsellingFeature && isUpsellingVisible && !accountHasAccessLevel;

        const requiredOnboardingSurvey = includes(Options, AccountOptions.RequiredOnboardingSurvey);
        const isAccountActive = isAccountActivated();

        if (!isAccountActive) {
            return <Redirect to={HomePaths.ConfirmEmail} />;
        }

        if (requiredOnboardingSurvey) {
            return <Route component={OnboardingSurvey} />;
        }

        if (canSeeUpselling) {
            const routeParams = omit(params, 'component');
            return <Route {...routeParams} render={() => <FeatureUpselling feature={params.upsellingFeature} />} />;
        }

        if (!accountHasAccessLevel) {
            console.log('Not permited.', params?.location?.pathname);
            return <Redirect to="/401" />;
        }
    }

    if (!params.account.Profile) {
        return null;
    }

    if (
        !preventProductSwitching &&
        bootedAsChild &&
        isStandalone &&
        DashboardPaths[account.GeneralInfo.ProductType] !== routerBasePath
    ) {
        window.location.pathname = changeDashboardPaths(location.pathname, account.GeneralInfo.ProductType);
        return null;
    }

    const Component = params.component;

    const linksToLoadComponent = [
        '#/templates/edit',
        '#/templates/create',
        '#/landingpages/edit',
        '#/landingpages/create',
    ];

    if (linksToLoadComponent.some((link) => params.location.hash.includes(link))) {
        return <Route {...params} component={DragDropEditor} />;
    }

    return <Route {...params} component={Component} />;
};

export default connect(mapState)(PrivateRoute);
